/**
 * Configuration governance — the lifecycle a configuration change goes
 * through before it can affect a posted transaction.
 *
 * The architecture is explicit that configuration is versioned and pinned:
 * every document records the config context that governed it, so "what were
 * the rules at the time?" has an answer. These screens are the human side of
 * that — Draft, Validate, Simulate, Approve, Activate, and Rollback.
 *
 * The ordering of the actions is the whole point and is enforced in the UI as
 * well as the API. Activate is offered last and separately, because it is the
 * step that starts affecting real transactions.
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
  StatusBadge,
} from "@/components/ui";
import { post, useApiList, useApiMutation, useApiQuery } from "@/lib/api/hooks";
import { formatDateTime } from "@/lib/money";

export interface EntityDefinitionRow {
  id: string;
  name: string;
  module: string | null;
  entity_type: string;
  storage_type: string | null;
  status: string;
  version: number;
  effective_from: string | null;
  effective_to: string | null;
  created_at: string;
  updated_at: string;
}

/** The order the lifecycle actually runs in. */
const LIFECYCLE = [
  "Draft",
  "Validated",
  "Simulated",
  "Approved",
  "Active",
] as const;

export function ConfigGovernancePage() {
  const definitions = useApiList<EntityDefinitionRow>(
    ["config", "entity-definitions"],
    "/entity-definitions",
  );

  if (definitions.isLoading) return <Skeleton rows={10} />;

  const rows = definitions.data?.items ?? [];
  const byStatus = (status: string) =>
    rows.filter((r) => r.status === status).length;
  const pendingApproval = rows.filter(
    (r) => r.status === "Validated" || r.status === "Simulated",
  );

  return (
    <div>
      <PageHeader
        title="Configuration governance"
        subtitle="Every configuration change is versioned, reviewed and pinned, so a posted transaction can always be traced to the rules that governed it."
      />

      <div className="atlas-grid atlas-grid--4 mb-4">
        <StatCard label="Draft" value={byStatus("Draft")} />
        <StatCard
          label="Awaiting approval"
          value={pendingApproval.length}
          tone={pendingApproval.length > 0 ? "negative" : undefined}
        />
        <StatCard label="Active" value={byStatus("Active")} />
        <StatCard
          label="Total definitions"
          value={definitions.data?.total ?? 0}
        />
      </div>

      <Card title="The lifecycle" className="mb-4">
        <ol className="atlas-lifecycle">
          {LIFECYCLE.map((stage, i) => (
            <li key={stage}>
              <strong>
                {i + 1}. {stage}
              </strong>
              <span className="atlas-muted">
                {stage === "Draft" && " — editable, affects nothing"}
                {stage === "Validated" && " — checked against core invariants"}
                {stage === "Simulated" &&
                  " — run against real data without committing"}
                {stage === "Approved" && " — signed off, not yet in force"}
                {stage === "Active" &&
                  " — governs new transactions from now on"}
              </span>
            </li>
          ))}
        </ol>
        <p className="atlas-muted" style={{ fontSize: 13, marginBottom: 0 }}>
          An activated definition is immutable. Changing it means a new version,
          which is why rollback restores a previous version rather than editing
          the current one.
        </p>
      </Card>

      {rows.length === 0 ? (
        <EmptyState
          title="No configuration definitions"
          message="Entity definitions are created in the entity builder. They appear here once they exist, and move through this lifecycle before they can affect a transaction."
          action={
            <Link to="/app/setup/entity-builder">
              <span className="atlas-btn atlas-btn--primary">
                Entity builder
              </span>
            </Link>
          }
        />
      ) : (
        <Card title="Definitions">
          <DataTable<EntityDefinitionRow>
            rows={rows}
            columns={[
              {
                key: "name",
                label: "Definition",
                render: (r) => (
                  <Link to={`/app/config/governance/changes/${r.id}`}>
                    {r.name}
                  </Link>
                ),
              },
              {
                key: "module",
                label: "Module",
                render: (r) => r.module ?? "—",
              },
              { key: "entity_type", label: "Entity type" },
              { key: "version", label: "Version", numeric: true },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
              {
                key: "updated_at",
                label: "Updated",
                render: (r) => formatDateTime(r.updated_at),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

export function ConfigChangesPage() {
  return (
    <div>
      <PageHeader
        title="Configuration changes"
        subtitle="Definitions that are not yet in force — drafts, validated changes and approvals waiting to be activated."
        breadcrumb={["Configuration", "Changes"]}
      />
      <ConfigDefinitionsTable
        exclude={["Active", "Superseded"]}
        emptyTitle="No pending changes"
        emptyMessage="Every definition is either active or archived. New changes start as drafts in the entity builder."
      />
    </div>
  );
}

export function ConfigVersionsPage() {
  return (
    <div>
      <PageHeader
        title="Configuration versions"
        subtitle="Activated versions, newest first. Each is immutable and is what a transaction pins to."
        breadcrumb={["Configuration", "Versions"]}
      />
      <ConfigDefinitionsTable
        only={["Active", "Superseded"]}
        emptyTitle="No activated versions"
        emptyMessage="Once a definition is activated it becomes a version here, and every transaction governed by it records which one."
      />
    </div>
  );
}

function ConfigDefinitionsTable({
  only,
  exclude,
  emptyTitle,
  emptyMessage,
}: {
  only?: string[];
  exclude?: string[];
  emptyTitle: string;
  emptyMessage: string;
}) {
  const definitions = useApiList<EntityDefinitionRow>(
    ["config", "entity-definitions"],
    "/entity-definitions",
  );
  if (definitions.isLoading) return <Skeleton rows={8} />;
  if (definitions.error)
    return (
      <EmptyState
        title="Could not load definitions"
        message={definitions.error.message}
      />
    );

  const rows = (definitions.data?.items ?? []).filter((r) => {
    if (only) return only.includes(r.status);
    if (exclude) return !exclude.includes(r.status);
    return true;
  });

  if (rows.length === 0)
    return <EmptyState title={emptyTitle} message={emptyMessage} />;

  return (
    <Card title={`Definitions (${rows.length})`}>
      <DataTable<EntityDefinitionRow>
        rows={rows}
        columns={[
          {
            key: "name",
            label: "Definition",
            render: (r) => (
              <Link to={`/app/config/governance/changes/${r.id}`}>
                {r.name}
              </Link>
            ),
          },
          { key: "entity_type", label: "Entity type" },
          { key: "version", label: "Version", numeric: true },
          {
            key: "status",
            label: "Status",
            render: (r) => <StatusBadge status={r.status} />,
          },
          {
            key: "effective_from",
            label: "Effective from",
            render: (r) =>
              r.effective_from ? formatDateTime(r.effective_from) : "—",
          },
        ]}
      />
    </Card>
  );
}

export function ConfigChangeDetailPage() {
  const { id = "" } = useParams();
  const [result, setResult] = useState<string | null>(null);

  const definition = useApiQuery<EntityDefinitionRow>(
    ["config", "entity-definition", id],
    `/entity-definitions/${id}`,
    { enabled: Boolean(id) },
  );

  const action = useApiMutation<string, unknown>(
    ["config", "entity-definition", id],
    (name) => post(`/entity-definitions/${id}/actions/${name}`, {}),
  );

  const run = (name: string, confirmation?: string) => {
    if (confirmation && !window.confirm(confirmation)) return;
    setResult(null);
    action.mutate(name, {
      onSuccess: () => setResult(`${name} completed.`),
    });
  };

  if (definition.isLoading) return <Skeleton rows={10} />;
  if (definition.error || !definition.data) {
    return (
      <EmptyState
        title="Definition not found"
        message={definition.error?.message ?? "It may have been removed."}
      />
    );
  }

  const row = definition.data;
  const busy = action.isPending;
  const isActive = row.status === "Active";

  return (
    <div>
      <PageHeader
        title={row.name}
        subtitle={`${row.entity_type} · version ${row.version}`}
        breadcrumb={["Configuration", "Changes", row.name]}
      />

      <Card title="Status" className="mb-4">
        <dl className="atlas-grid atlas-grid--4">
          <div>
            <dt className="atlas-type-label">Status</dt>
            <dd>
              <StatusBadge status={row.status} />
            </dd>
          </div>
          <div>
            <dt className="atlas-type-label">Module</dt>
            <dd>{row.module ?? "—"}</dd>
          </div>
          <div>
            <dt className="atlas-type-label">Storage</dt>
            <dd>{row.storage_type ?? "—"}</dd>
          </div>
          <div>
            <dt className="atlas-type-label">Effective from</dt>
            <dd>
              {row.effective_from
                ? formatDateTime(row.effective_from)
                : "Not in force"}
            </dd>
          </div>
        </dl>
      </Card>

      <Card title="Lifecycle actions">
        <p className="atlas-muted" style={{ fontSize: 13 }}>
          Validate and simulate are safe to run repeatedly — neither changes
          data. Approve records sign-off. Activate is the step that starts
          governing real transactions.
        </p>
        <div className="atlas-flex atlas-flex--gap-2 atlas-flex--wrap">
          <Button disabled={busy || isActive} onClick={() => run("validate")}>
            Validate
          </Button>
          <Button disabled={busy || isActive} onClick={() => run("simulate")}>
            Simulate
          </Button>
          <Button disabled={busy || isActive} onClick={() => run("approve")}>
            Approve
          </Button>
          <Button
            variant="primary"
            disabled={busy || isActive}
            onClick={() =>
              run(
                "activate",
                `Activate ${row.name}?\n\nFrom this point every new transaction is governed by this version and pins to it. The definition becomes immutable — a further change means a new version.`,
              )
            }
          >
            Activate
          </Button>
          <Button
            variant="danger"
            disabled={busy}
            onClick={() =>
              run(
                "rollback",
                `Roll back ${row.name}?\n\nThis restores the previous version for new transactions. Transactions already posted keep the version they were pinned to and are not rewritten.`,
              )
            }
          >
            Roll back
          </Button>
        </div>
        {action.error && (
          <p style={{ color: "var(--atlas-danger)", fontSize: 13 }}>
            {action.error.message}
          </p>
        )}
        {result && (
          <p style={{ color: "var(--atlas-success)", fontSize: 13 }}>
            {result}
          </p>
        )}
      </Card>
    </div>
  );
}
